import axios, { AxiosInstance } from 'axios';
import { Backup, BackupSchedule } from '../types';

export class BackupService {
  private client: AxiosInstance;

  constructor(baseUrl: string = 'http://localhost:8080/api') {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Get all backups
  async getBackups(namespace?: string): Promise<Backup[]> {
    try {
      const url = namespace
        ? `/v1/namespaces/${namespace}/backups`
        : `/v1/backups`;
      const response = await this.client.get(url);
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to get backups failed',
        _error,
      );
      return this.getMockBackups();
    }
  }

  // Get backups for a specific database cluster
  async getClusterBackups(
    namespace: string,
    dbClusterName: string,
  ): Promise<Backup[]> {
    try {
      const response = await this.client.get(
        `/v1/namespaces/${namespace}/database-clusters/${dbClusterName}/backups`,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to get backups for cluster '${namespace}/${dbClusterName}' failed`,
        _error,
      );
      return this.getMockBackups().filter(
        b => b.spec.dbClusterName === dbClusterName,
      );
    }
  }

  // Create an on-demand backup
  async createBackup(
    namespace: string,
    dbClusterName: string,
    backupStorageName: string,
  ): Promise<Backup> {
    const backup: Backup = {
      metadata: {
        name: `${dbClusterName}-manual-${Date.now()}`,
        namespace,
      },
      spec: {
        dbClusterName,
        backupStorageName,
      },
    };

    try {
      const response = await this.client.post(
        `/v1/namespaces/${namespace}/backups`,
        backup,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to create backup failed',
        _error,
      );
      return {
        ...backup,
        status: {
          state: 'starting',
        },
      };
    }
  }

  // Delete a backup
  async deleteBackup(namespace: string, name: string): Promise<void> {
    try {
      await this.client.delete(`/v1/namespaces/${namespace}/backups/${name}`);
    } catch (_error) {
      console.log(`Mock: Deleted backup ${namespace}/${name}`);
    }
  }

  // Get backup schedules for a cluster
  async getBackupSchedules(
    namespace: string,
    dbClusterName: string,
  ): Promise<BackupSchedule[]> {
    try {
      const response = await this.client.get(
        `/v1/namespaces/${namespace}/database-clusters/${dbClusterName}/backup-schedules`,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to get backup schedules for cluster '${namespace}/${dbClusterName}' failed`,
        _error,
      );
      return this.getMockSchedules();
    }
  }

  // Update backup schedule
  async updateBackupSchedule(
    namespace: string,
    dbClusterName: string,
    schedule: BackupSchedule,
  ): Promise<BackupSchedule> {
    try {
      const response = await this.client.put(
        `/v1/namespaces/${namespace}/database-clusters/${dbClusterName}/backup-schedules/${schedule.name}`,
        schedule,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to update backup schedule for cluster '${namespace}/${dbClusterName}' failed`,
        _error,
      );
      return schedule;
    }
  }

  // Mock data helpers
  private getMockBackups(): Backup[] {
    return [
      {
        metadata: {
          name: 'pg-production-2025-02-01-020000',
          namespace: 'everest',
          creationTimestamp: '2025-02-01T02:00:00Z',
        },
        spec: {
          dbClusterName: 'pg-production',
          backupStorageName: 's3-backup',
        },
        status: {
          state: 'succeeded',
          completed: '2025-02-01T02:15:23Z',
          destination: 's3://my-backups/pg-production/2025-02-01-020000',
          size: 5368709120, // 5GB in bytes
        },
      },
      {
        metadata: {
          name: 'pg-production-2025-01-31-020000',
          namespace: 'everest',
          creationTimestamp: '2025-01-31T02:00:00Z',
        },
        spec: {
          dbClusterName: 'pg-production',
          backupStorageName: 's3-backup',
        },
        status: {
          state: 'succeeded',
          completed: '2025-01-31T02:14:56Z',
          destination: 's3://my-backups/pg-production/2025-01-31-020000',
          size: 5242880000, // 4.88GB
        },
      },
      {
        metadata: {
          name: 'mongo-analytics-2025-02-02-010000',
          namespace: 'everest',
          creationTimestamp: '2025-02-02T01:00:00Z',
        },
        spec: {
          dbClusterName: 'mongo-analytics',
          backupStorageName: 's3-backup',
        },
        status: {
          state: 'running',
        },
      },
      {
        metadata: {
          name: 'mongo-analytics-2025-02-01-230000',
          namespace: 'everest',
          creationTimestamp: '2025-02-01T23:00:00Z',
        },
        spec: {
          dbClusterName: 'mongo-analytics',
          backupStorageName: 's3-backup',
        },
        status: {
          state: 'succeeded',
          completed: '2025-02-01T23:22:45Z',
          destination: 's3://my-backups/mongo-analytics/2025-02-01-230000',
          size: 18253611008, // 17GB
        },
      },
    ];
  }

  private getMockSchedules(): BackupSchedule[] {
    return [
      {
        name: 'daily-backup',
        enabled: true,
        schedule: '0 2 * * *',
        backupStorageName: 's3-backup',
        retentionCopies: 7,
      },
      {
        name: 'weekly-backup',
        enabled: true,
        schedule: '0 0 * * 0',
        backupStorageName: 's3-backup',
        retentionCopies: 4,
      },
    ];
  }
}

export const backupService = new BackupService();
